import { useState } from "react";
import type { ClipMedia, Moment } from "@coaching/types";
import { clock, clockPrecise } from "../format";
import { Icon } from "./icons";

/** A moment's pin takes the colour of its first drawing, so the strip reads like the frames. */
const pinColour = (m: Moment) => m.shapes[0]?.color ?? "var(--accent)";

/**
 * The bucket as one track: each clip a segment sized by its length with a gap
 * between, the played part filled, the playhead as a knob, and every moment a
 * pin above its place. Hovering shows the time under the pointer; clicking
 * seeks there, switching clip when needed.
 */
export function ClipStrip({ clips, clipIndex, time, paused, moments, activeId, onSeek, onTogglePlay, onStep, onNudge, onSelect, onMark, canMark }: {
    clips: ClipMedia[];
    clipIndex: number;
    time: number;
    paused: boolean;
    moments: Moment[];
    activeId: string | null;
    onSeek: (clipIndex: number, t: number) => void;
    onTogglePlay: () => void;
    onStep: (frames: number) => void;
    onNudge: (sec: number) => void;
    onSelect: (m: Moment) => void;
    onMark: () => void;
    canMark: boolean;
}) {
    const [hover, setHover] = useState<{ clip: number; frac: number } | null>(null);
    const total = clips.reduce((s, c) => s + c.durationSec, 0);
    const offsets = clips.map((_, i) => clips.slice(0, i).reduce((s, c) => s + c.durationSec, 0));
    const current = clips[clipIndex];
    const position = (offsets[clipIndex] ?? 0) + time;

    const fracAt = (e: React.MouseEvent<HTMLElement>) => {
        const r = e.currentTarget.getBoundingClientRect();
        return r.width > 0 ? Math.min(1, Math.max(0, (e.clientX - r.left) / r.width)) : 0;
    };

    return (
        <div className="timeline">
            <div className="transport">
                <div className="transport-buttons">
                    <button type="button" className="ctl" onClick={() => onNudge(-1)} title="Back one second ([)" aria-label="Back one second"><Icon.secondBack /></button>
                    <button type="button" className="ctl" onClick={() => onStep(-1)} title="Back one frame (,)" aria-label="Back one frame"><Icon.frameBack /></button>
                    <button type="button" className="ctl play" onClick={onTogglePlay} title="Play or pause (Space)" aria-label={paused ? "Play" : "Pause"}>{paused ? <Icon.play /> : <Icon.pause />}</button>
                    <button type="button" className="ctl" onClick={() => onStep(1)} title="Forward one frame (.)" aria-label="Forward one frame"><Icon.frameForward /></button>
                    <button type="button" className="ctl" onClick={() => onNudge(1)} title="Forward one second (])" aria-label="Forward one second"><Icon.secondForward /></button>
                </div>
                <div className="clock" data-testid="clock">
                    <span className="clock-now mono">{clockPrecise(time)}</span>
                    <span className="clock-of mono">/ {current ? clock(current.durationSec) : "0:00"}</span>
                    <span className="clock-clip">Clip {clipIndex + 1} of {clips.length}</span>
                    <span className="clock-total mono">{clock(position)} of {clock(total)}</span>
                </div>
                <button type="button" className="btn primary small mark" onClick={onMark} disabled={!canMark} title="Mark this frame as a moment (M)">
                    <Icon.plus /> Add moment
                </button>
            </div>

            <div className="track" role="group" aria-label="Clips">
                {clips.map((c, i) => {
                    const own = moments.filter((m) => m.clipId === c.clipId);
                    const playing = i === clipIndex;
                    const played = i < clipIndex ? 1 : playing ? Math.min(1, time / Math.max(c.durationSec, 0.1)) : 0;
                    const hovering = hover?.clip === i ? hover.frac : null;
                    return (
                        <div key={c.clipId} className={`segment${playing ? " on" : ""}`} style={{ flexGrow: Math.max(c.durationSec, 1) }}>
                            <span className="segment-label">{i + 1}</span>
                            <button type="button" className="segment-bar" data-testid={`strip-clip-${i}`}
                                title={`Clip ${i + 1}: ${c.title}, ${clock(c.durationSec)}`} aria-label={`Clip ${i + 1}, ${clock(c.durationSec)}`}
                                onClick={(e) => onSeek(i, fracAt(e) * c.durationSec)}
                                onMouseMove={(e) => setHover({ clip: i, frac: fracAt(e) })} onMouseLeave={() => setHover(null)}>
                                <span className="segment-fill" style={{ width: `${played * 100}%` }} />
                                {hovering !== null && <span className="segment-hover" style={{ left: `${hovering * 100}%` }} />}
                            </button>
                            {hovering !== null && <span className="hover-time mono" style={{ left: `${hovering * 100}%` }}>{clock(hovering * c.durationSec)}</span>}
                            {playing && <span className="knob" style={{ left: `${played * 100}%` }} aria-hidden />}
                            {own.map((m) => (
                                <button key={m.id} type="button" className={`pin${m.id === activeId ? " on" : ""}`} style={{ left: `${(m.t / Math.max(c.durationSec, 0.1)) * 100}%`, "--pin": pinColour(m) } as React.CSSProperties}
                                    title={`${m.label || "Moment"} · ${clock(m.t)}${m.voice ? " · voice note" : ""}`} aria-label={`${m.label || "Moment"} at ${clock(m.t)} in clip ${i + 1}`}
                                    onClick={() => onSelect(m)} />
                            ))}
                        </div>
                    );
                })}
            </div>
        </div>
    );
}
